export function createRectangleFrom3Points(A, B, C) {
  const dx = B[0] - A[0];
  const dy = B[1] - A[1];
  const L = Math.sqrt(dx * dx + dy * dy);

  const nx = -dy / L;
  const ny = dx / L;

  const ACx = C[0] - A[0];
  const ACy = C[1] - A[1];

  const h = ACx * nx + ACy * ny;

  const D = [B[0] + h * nx, B[1] + h * ny];
  const C2 = [A[0] + h * nx, A[1] + h * ny];

  return [A, B, D, C2, A]; // closed polygon
}

export function projectPointOntoLine(A, vec, P) {
  const len2 = vec[0] ** 2 + vec[1] ** 2;
  const t = ((P[0] - A[0]) * vec[0] + (P[1] - A[1]) * vec[1]) / len2;
  return [A[0] + t * vec[0], A[1] + t * vec[1]];
}

// Reorder the corners of a rectangle so that edge 0 (rect[0]→rect[1])
// is the LONG edge of the rectangle (parallel edge 2 will also be long).
//
// ⚠️ Same coordinate-system requirement as getMidfieldLine: input MUST
// be planar (UTM), not WGS84 lng/lat — see warning above.
//
//
// Why this matters: the mast-layout config in src/config/mastlayouts.js
// uses edges[i] ∈ {0, 2} for every mast — i.e. masts are placed along
// edges 0 and 2 of the rectangle and never along edges 1 / 3. The
// layout assumes those two edges are the LONG SIDES of the field.
//
// But createRectangleFrom3Points returns the corners in the order the
// USER drew them: rect[0]→rect[1] is whichever edge the user started
// with. If they drew the short side first, edge 0 is short and the
// layout drops every mast onto the short ends (the bug Philip showed
// on 2026-05-20 — masts clustered at the short ends of the pitch).
//
// We fix it once, at the source: rotate the corner list by one
// position so that edge 0 is always the long edge. Geometrically the
// same rectangle — only the "starting corner" used for indexing
// changes — so the rendered shape is identical but the layout's edge
// references now hit the long sides.
//
// Input  : [A, B, D, C2] (4 corners; a closing fifth point is optional)
// Output : same 4 corners, possibly rotated by 1, with the closing
//          point preserved if it was present in the input.
export function normalizeRectLongFirst(rect) {
  if (!rect || rect.length < 4) return rect;
  const A  = rect[0];
  const B  = rect[1];
  const D  = rect[2];
  const C2 = rect[3];
  const L = Math.hypot(B[0] - A[0], B[1] - A[1]); // edge 0 (AB)  length
  const h = Math.hypot(D[0] - B[0], D[1] - B[1]); // edge 1 (BD)  length
  if (L >= h) {
    // Edge 0 already the long side — no rotation needed.
    return rect;
  }
  // Edge 0 is the short side; rotate corners by 1 so edge 0 becomes BD.
  // New order: [B, D, C2, A] (and re-close with B if the input was closed).
  const rotated = [B, D, C2, A];
  if (rect.length === 5) rotated.push(B);
  return rotated;
}

// Sortiert die Ecken so um, dass Kante 0 (rect[0]→rect[1]) PARALLEL zur
// uebergebenen Achse liegt — gedacht fuer die Laengsachse des SPIELFELDS.
//
// ⚠️ Wie normalizeRectLongFirst: Eingabe MUSS planar sein (UTM).
//
// Warum das noetig ist (Signify-Tests auf der Sandbox, 21.09.2026): die
// Presets in config/mastlayouts.js setzen die Masten auf die Kanten 0 und
// 2 und meinen damit die LANGSEITEN DES FELDES. normalizeRectLongFirst
// nahm als Ersatz dafuer die laengere Kante des MASTRECHTECKS — das ist
// nur dann dasselbe, wenn das Rechteck laenger als breit gezeichnet wird.
// Die Tester zogen es aber bei den eingerueckten Presets (0·2·2·0) und
// bei Eckmasten weit innerhalb der Feldenden (2·1·2, 1·2·1) KUERZER als
// breit: entlang des Feldes 40–70 m, quer 64–75 m. Damit wurde die
// Querkante zur "Langseite", alle Masten landeten auf den Stirnseiten
// bzw. mitten im Feld ("8 Masten liegen im Spielfeld"). Die Feldachse
// ist die verlaessliche Groesse, nicht das Seitenverhaeltnis des
// Rechtecks — deshalb entscheidet ab jetzt sie.
//
// Rotiert den Ring hoechstens um eine Position (wie normalizeRectLongFirst),
// der Umlaufsinn bleibt erhalten. Ohne brauchbare Achse (null, Nullvektor)
// faellt die Funktion auf normalizeRectLongFirst zurueck.
export function normalizeRectAlongAxis(rect, axis) {
  if (!rect || rect.length < 4) return rect;
  const alen = axis ? Math.hypot(axis[0], axis[1]) : 0;
  if (!alen || !isFinite(alen)) return normalizeRectLongFirst(rect);
  const A = rect[0], B = rect[1], D = rect[2], C2 = rect[3];
  // |cos| des Winkels zwischen Kante und Achse — je groesser, desto paralleler.
  const parallel = (P, Q) => {
    const vx = Q[0] - P[0], vy = Q[1] - P[1];
    const vlen = Math.hypot(vx, vy);
    return vlen ? Math.abs((vx * axis[0] + vy * axis[1]) / (vlen * alen)) : 0;
  };
  if (parallel(A, B) >= parallel(B, D)) return rect;
  const rotated = [B, D, C2, A];
  if (rect.length === 5) rotated.push(B);
  return rotated;
}

// Bringt die Ecken eines Rechtecks in einen FESTEN Umlaufsinn: im
// Uhrzeigersinn, gesehen auf der Karte (x = Ost, y = Nord).
//
// ⚠️ Wie normalizeRectLongFirst: Eingabe MUSS planar sein (UTM), nicht
// WGS84 lng/lat.
//
// Warum das noetig ist (Philips Fund 25.08.2026, "Leuchten schauen weg
// vom Feld"): der Umlaufsinn des gezeichneten Rechtecks haengt davon ab,
// in welcher Richtung der Nutzer die erste Kante zieht und auf welcher
// Seite der dritte Klick landet. Die Layout-Winkel in
// config/mastlayouts.js setzen aber einen festen Umlaufsinn voraus —
// die Preset-Blickrichtung ist "Kantenrichtung um (Winkel − 90°)
// gedreht", und das zeigt nur bei EINEM Umlaufsinn ins Feld. Beim
// anderen zeigt sie nach aussen; _captureAngleOffsets friert diesen
// ~180°-Versatz dann als Ausrichtungsabsicht ein, und alle Pfeile
// stehen dauerhaft verkehrt.
//
// Der richtige Umlaufsinn ist der im Uhrzeigersinn: dann liegt das
// Feldinnere RECHTS der Kantenrichtung, und "Kantenrichtung − 90°"
// (= rechts abbiegen) weist hinein.
//
// Die Umkehr behaelt Ecke 0 als Startecke und laeuft den Ring rueckwaerts
// — dieselben vier Ecken, nur andersherum verkettet. Danach muss der
// Aufrufer normalizeRectLongFirst erneut anwenden koennen (die Umkehr
// tauscht, welche Kante die Nummer 0 traegt).
export function normalizeRectWinding(rect) {
  if (!rect || rect.length < 4) return rect;
  const pts = rect.slice(0, 4);
  // Schnuersenkelformel: positiv = gegen den Uhrzeigersinn (bei x-Ost/y-Nord).
  let area2 = 0;
  for (let i = 0; i < 4; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % 4];
    area2 += x1 * y2 - x2 * y1;
  }
  if (area2 <= 0) {
    // Schon im Uhrzeigersinn — nichts zu tun.
    return rect;
  }
  const reversed = [pts[0], pts[3], pts[2], pts[1]];
  if (rect.length === 5) reversed.push(pts[0]);
  return reversed;
}

// Returns the midfield (center) line of a sports field rectangle.
//
// ⚠️ COORDINATE SYSTEM: the input rect MUST be in a PLANAR coordinate
// system (UTM or similar where Euclidean distance equals true distance).
// Do NOT pass WGS84 lng/lat directly — at non-equatorial latitudes,
// 1° lng ≠ 1° lat in metres, so the L >= h length comparison below
// can pick the WRONG edge pair as "long" and produce a midline parallel
// to the long axis instead of orthogonal to it (bug Philip hit on
// 2026-05-21 on N-S oriented pitches). The callers in
// FieldRectangleTool and MastRectangleTool project to UTM before calling
// this function — keep doing that.
//
// CORRECT BEHAVIOUR: The midline must always BISECT the LONGER pair of
// edges — i.e. it runs perpendicular to the long axis, splitting the
// field into two equal halves like a football centre line.
//
// Geometric reminder (AB = the first edge drawn, parallel to DC2;
// BD = the side adjacent to B, parallel to C2A):
//   * Connecting midpoints of AB and DC2 → line parallel to BD/C2A
//     (so this line is along the AXIS THAT BD/C2A point along).
//   * Connecting midpoints of BD and C2A → line parallel to AB/DC2
//     (so this line is along the AXIS THAT AB/DC2 point along).
// We want the line to be SHORT and to bisect the LONG edges, which
// means it should be parallel to the SHORT edges. Therefore we connect
// the midpoints of the SHORT edge pair (which sits *across* the long
// edges, splitting them at their midpoints).
//
// First fix (2026-04-20) tried to use this idea but inverted the picks
// — it connected the SHORT-edges' midpoints to each other, which gives
// a line PARALLEL to the long axis (the bug Philip showed on
// 2026-05-20 — the midline ran along the field's length instead of
// across it). Fixed properly here.
export function getMidfieldLine(rect) {
  if (!rect || rect.length < 4) return null;

  // Corners as produced by createRectangleFrom3Points: [A, B, D, C2, A]
  // Edge pairs:  (AB, DC2)  with length L = |B-A|
  //              (BD, C2A)  with length h = |D-B|
  const A  = rect[0];
  const B  = rect[1];
  const D  = rect[2];
  const C2 = rect[3];

  const L = Math.hypot(B[0] - A[0], B[1] - A[1]); // length of edge AB
  const h = Math.hypot(D[0] - B[0], D[1] - B[1]); // length of edge BD

  // We want to bisect the LONG edges. The midpoints we connect are the
  // midpoints of those long edges; connecting them produces a line that
  // is parallel to the SHORT edges (= perpendicular to the long axis).
  let p1, p2;
  if (L >= h) {
    // AB and DC2 are the LONG edges → bisect THEM.
    // Connecting their midpoints gives a line parallel to the short
    // edges BD and C2A, which is what we want.
    p1 = [(A[0] + B[0]) / 2,  (A[1] + B[1]) / 2];
    p2 = [(D[0] + C2[0]) / 2, (D[1] + C2[1]) / 2];
  } else {
    // BD and C2A are the LONG edges → bisect THEM.
    p1 = [(B[0] + D[0]) / 2,  (B[1] + D[1]) / 2];
    p2 = [(C2[0] + A[0]) / 2, (C2[1] + A[1]) / 2];
  }

  return [p1, p2];
}
